import { createContext, useId, type ReactNode } from 'react';

/**
 * What a field tells the control inside it.
 *
 * The control never invents its own id or `aria-describedby`: the field owns
 * those, because the field is what knows whether a hint and an error exist.
 */
export interface FieldWiring {
  /** The id the control must carry so the label's `for` points at it. */
  readonly controlId: string;
  /** Space-separated ids of the hint and error, or undefined when neither exists. */
  readonly describedBy: string | undefined;
  /** Whether the control should report itself invalid. */
  readonly invalid: boolean;
  /** Whether the control is required. */
  readonly required: boolean;
}

export const FieldContext = createContext<FieldWiring | null>(null);

export interface FormFieldProps {
  /** The visible label. Always visible — placeholder-as-label is not supported. */
  readonly label: ReactNode;
  /** Guidance shown under the control and referenced by `aria-describedby`. */
  readonly hint?: ReactNode;
  /**
   * A validation message.
   *
   * Presence of this prop is what makes the field invalid; there is no separate
   * `invalid` flag to fall out of step with the message.
   */
  readonly error?: ReactNode;
  /** Marks the field required, visually and in the accessibility tree. */
  readonly required?: boolean;
  /** The control. */
  readonly children: ReactNode;
}

/**
 * Label, control, hint and validation message, wired together.
 *
 * The wiring is the whole point. Hand-rolled forms drift — an id changes, a
 * hint stops being announced, an error is shown in red and nowhere else — so
 * this component generates the ids, points the label at the control, collects
 * the hint and the error into `aria-describedby`, and marks the error
 * `role="alert"` so it is spoken when it appears rather than when the user
 * happens to arrive at the field.
 *
 * @param props see {@link FormFieldProps}
 * @returns the field
 */
export function FormField({
  label,
  hint,
  error,
  required = false,
  children,
}: FormFieldProps): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;

  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined;

  return (
    <FieldContext value={{ controlId, describedBy, invalid: error !== undefined, required }}>
      <div
        className="fo-field"
        data-invalid={error === undefined ? undefined : ''}
        data-required={required ? '' : undefined}
      >
        {/* The required marker is drawn by CSS rather than inserted as text.
            A literal asterisk inside the label becomes part of the control's
            accessible name — every screen reader then says "Email address star"
            and every test has to match "Email address*". The requirement is
            already carried properly by the control's own `required` attribute;
            the asterisk is decoration, so it is drawn as decoration. */}
        <label className="fo-field__label" htmlFor={controlId}>
          {label}
        </label>

        {children}

        {hint !== undefined ? (
          <p className="fo-field__hint" id={hintId}>
            {hint}
          </p>
        ) : null}

        {error !== undefined ? (
          <p className="fo-field__error" id={errorId} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext>
  );
}
